<template>
    <component :is="el" v-bind="animRoot" class="flex flex-col gap-5">
        <intelligent-inbox-wizard-card :expandable="props.expandable">
            <template v-slot:header>
                <component :is="el" v-bind="animProps">Status Definitions</component>
            </template>
            <template v-slot:headerSubtitle>
                <component :is="el" v-bind="animProps"
                    >Define lifecycle and member statuses the AI will use to classify contacts.</component
                >
            </template>
            <template v-slot:content>
                <div class="flex flex-col gap-6">
                    <component :is="el" v-bind="animProps" v-for="config in statusConfigs" :key="config.key">
                        <div class="flex items-center justify-between mb-3">
                            <vx-label class="!mb-0">{{ config.label }}</vx-label>
                            <vx-button color="muted" size="xs" @click="addStatus(config.key)">
                                <font-awesome-icon widthAuto :icon="faPlus"></font-awesome-icon>
                                Add
                            </vx-button>
                        </div>
                        <div class="flex flex-col gap-2">
                            <vx-box v-for="(status, index) in props.modelValue[config.key]" :key="index" class="p-4">
                                <div class="flex items-start gap-3">
                                    <div class="flex-1 flex flex-col gap-2">
                                        <vx-input
                                            :modelValue="status.name"
                                            @update:modelValue="updateStatus(config.key, index, 'name', $event)"
                                            size="md"
                                            placeholder="Status name"
                                        ></vx-input>
                                        <vx-input
                                            :modelValue="status.description"
                                            @update:modelValue="updateStatus(config.key, index, 'description', $event)"
                                            size="md"
                                            placeholder="Description"
                                            autoresize
                                        ></vx-input>
                                    </div>
                                    <vx-button
                                        v-if="props.modelValue[config.key].length > 1"
                                        color="muted"
                                        size="xs"
                                        @click="removeStatus(config.key, index)"
                                    >
                                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                                    </vx-button>
                                </div>
                            </vx-box>
                        </div>
                    </component>
                </div>
            </template>
        </intelligent-inbox-wizard-card>
    </component>
</template>

<script lang="ts" setup>
import { faPlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxInput, VxLabel } from '@voxie/frontend-components';
import IntelligentInboxWizardCard from './IntelligentInboxWizardCard.vue';
import { motion } from 'motion-v';
import { computed } from 'vue';
import type { WizardData, StatusEntry } from '../useIntelligentInbox';
import { fadeUpVariants, fieldStagger, quickTransition } from './wizardMotionVariants';

const props = withDefaults(
    defineProps<{
        modelValue: WizardData;
        animated?: boolean;
        expandable?: boolean;
    }>(),
    {
        animated: true,
        expandable: true,
    }
);

const el = computed(() => (props.animated ? motion.div : ('div' as const)));
const animRoot = computed(() =>
    props.animated ? { initial: 'hidden', animate: 'visible', transition: fieldStagger } : {}
);
const animProps = computed(() => (props.animated ? { variants: fadeUpVariants, transition: quickTransition } : {}));

const emit = defineEmits<{
    'update:modelValue': [value: WizardData];
}>();

type StatusListKey = 'lifecycleStatuses' | 'memberStatuses';

const statusConfigs: { key: StatusListKey; label: string }[] = [
    { key: 'lifecycleStatuses', label: 'Lifecycle Statuses' },
    { key: 'memberStatuses', label: 'Member Statuses' },
];

const updateStatus = (listKey: StatusListKey, index: number, key: keyof StatusEntry, value: string | number): void => {
    const statuses: StatusEntry[] = props.modelValue[listKey].map((s, i) =>
        i === index ? { ...s, [key]: String(value) } : s
    );
    emit('update:modelValue', { ...props.modelValue, [listKey]: statuses });
};

const addStatus = (listKey: StatusListKey): void => {
    emit('update:modelValue', {
        ...props.modelValue,
        [listKey]: [...props.modelValue[listKey], { name: '', description: '' }],
    });
};

const removeStatus = (listKey: StatusListKey, index: number): void => {
    const statuses = props.modelValue[listKey].filter((_: StatusEntry, i: number) => i !== index);
    emit('update:modelValue', { ...props.modelValue, [listKey]: statuses });
};
</script>
